import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'ncoded-component-library';
import { Field, useForm, useFormState } from 'react-final-form';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import ResolvePopup from 'components/ResolvePopup';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  getCheckedValuesInObject,
  getJoinedWordsWithComma,
  getUserFullName,
} from 'utils';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import { BuyerEnquiryHistory, PreviousDeal } from 'models/DealRoom';
import { SORT_VALUES } from '../../utils';
import usePastTime from 'hooks/usePastTime';
import InfiniteScroll from 'components/InfiniteScroll';
import { InfiniteScrollRef } from 'components/InfiniteScroll/InfiniteScroll.component';
import VirtualArrayRender from 'components/VirtualArrayRender';

import './SelectLeads.styles.scss';

type SelectLeadsProps = {
  className?: string;
  sort: string;
  leads: BuyerEnquiryHistory[];
  onContainerScroll: () => void;
  updateSort: (sort: string) => void;
};

const SelectLeads: React.FC<SelectLeadsProps> = (props) => {
  const { className, leads, sort, updateSort, onContainerScroll } = props;

  const [infiniteScroll, infiniteScrollRef] =
    useCallbackRef<InfiniteScrollRef>(null);

  const classes = classNames('select-leads', className);

  const { t } = useTranslation();

  const pastTime = usePastTime();

  const {
    values: { leads: formLeads },
  } = useFormState();
  const { change, batch } = useForm();

  const checkedLeadsLength = useMemo(
    () => (getCheckedValuesInObject(formLeads || {}) || []).length,
    [formLeads],
  );

  const areAllSelected = useMemo(
    () => checkedLeadsLength === leads.length,
    [checkedLeadsLength, leads.length],
  );

  const onAllSelect = useCallback(
    () =>
      batch(() =>
        leads.forEach(({ buyerId }) =>
          change(`leads.${buyerId}`, !areAllSelected),
        ),
      ),
    [areAllSelected, leads, batch, change],
  );

  const getPreviousEnquiries = useCallback(
    (previousEnquiries: PreviousDeal[]) => (
      <p>
        {getJoinedWordsWithComma(
          previousEnquiries.map(({ dealName }) => dealName),
        )}
      </p>
    ),
    [],
  );

  const renderLead = useCallback(
    ({
      buyerId,
      buyerFirstName,
      buyerLastName,
      buyerEmail,
      lastEnquire,
      previousDeals,
    }: BuyerEnquiryHistory) => (
      <>
        <Field
          name={`leads.${buyerId}`}
          type="checkbox"
          component={CheckboxField}
          label={
            <div className="name">
              {getUserFullName(buyerFirstName, buyerLastName) || buyerEmail}
            </div>
          }
        />
        <div className="previous-deals">
          <ResolvePopup
            onHover
            popupContent={getPreviousEnquiries(previousDeals)}
          >
            {previousDeals?.length}
          </ResolvePopup>
        </div>
        <div className="last-enquire">{pastTime(lastEnquire)}</div>
      </>
    ),
    [getPreviousEnquiries, pastTime],
  );

  return (
    <div className={classes}>
      <div className="sorting-buttons">
        <Checkbox checked={areAllSelected} onChange={onAllSelect} />

        <SortingButton
          content={t('lead')}
          sortingValue={SORT_VALUES.LEAD}
          sort={sort}
          updateSort={updateSort}
        />
        <SortingButton
          content={t('numberOfPreviousEnquiries')}
          sortingValue={SORT_VALUES.NUMBER_OF_ENQUIRIES}
          sort={sort}
          updateSort={updateSort}
        />
        <SortingButton
          content={t('lastEnquiry')}
          sortingValue={SORT_VALUES.LAST_ENQUIRE}
          sort={sort}
          updateSort={updateSort}
        />
      </div>

      <InfiniteScroll
        ref={infiniteScrollRef}
        className="leads"
        onScroll={onContainerScroll}
        localScroll
      >
        <VirtualArrayRender
          items={leads}
          estimateSize={34}
          getScrollElement={() => infiniteScroll?.container}
          getItemKey={({ buyerId }) => buyerId}
          renderItem={(lead) => (
            <div className="lead-row">{renderLead(lead)}</div>
          )}
        />
      </InfiniteScroll>

      {!checkedLeadsLength && (
        <Field
          name="leads"
          hidden
          component="input"
          validate={() => t('requiredField')}
        />
      )}
    </div>
  );
};

export default SelectLeads;
